import { ZodiacEngine } from "./zodiacEngine";
import { MCTSController_v2 } from "./MCTS_v2/core/MCTSController_v2";
import { IMove, Position } from "./types";
import { HeuristicInterface, HeuristicType, getHeuristic } from "./heuristics";
import { ZodiacFEN } from "./Fen/ZodiacFEN";
import TimeControlManager from "../timeControl/TimeControlManager";

/**
 * Interface for AI performance statistics
 */
export interface AIStats {
  nodesByDepth: Map<number, number>;
  totalNodesVisited: number;
  executionTimeMs: number;
  heuristicUsed: string;
  maxDepth: number;
}

/**
 * Provides AI functionality to the Zodiac game
 *
 * This controller uses the Monte Carlo Tree Search algorithm
 * to find the best move for the AI player.
 */
export class AIController {
  public mcts: MCTSController_v2;
  private searchIterations: number = 1000; // Increased from 500 to 1000
  private lastMoveStats: AIStats | null = null;
  private verboseLogging: boolean = true;
  private heuristic: HeuristicInterface;
  private maxDepth: number = 8; // Increased from 4 to 8 for deeper search

  /**
   * Creates a new AI controller
   * @param zodiacEngine Reference to the main game engine
   */
  constructor(private zodiacEngine: ZodiacEngine) {
    // Set default heuristic to Tactical for testing
    this.heuristic = getHeuristic(HeuristicType.Tactical);

    // Initialize MCTS with the heuristic

    this.mcts = new MCTSController_v2(this.zodiacEngine);

    // Pass the heuristic to MCTS
    this.mcts.setHeuristic(this.heuristic);

    // Set the max depth for depth-limited search
    this.mcts.setMaxDepth(this.maxDepth);

    // Enable verbose logging to debug monkey-frog-tree interactions
    this.verboseLogging = true;
    this.mcts.setVerboseLogging(true);

    // Initialize stats
    this.lastMoveStats = {
      nodesByDepth: new Map<number, number>(),
      totalNodesVisited: 0,
      executionTimeMs: 0,
      heuristicUsed: this.heuristic.name,
      maxDepth: this.maxDepth,
    };
  }

  /**
   * Set the maximum depth for the depth-limited search
   * @param depth The maximum depth to search to
   */
  setMaxDepth(depth: number): void {
    if (depth < 1) {
      this.maxDepth = 1;
    } else {
      this.maxDepth = depth;
    }
    // Also update the MCTS max depth
    this.mcts.setMaxDepth(this.maxDepth);

    if (this.verboseLogging) {
    }
  }

  /**
   * Get the current maximum search depth
   * @returns Current maximum search depth
   */
  getMaxDepth(): number {
    return this.maxDepth;
  }

  /**
   * Set the heuristic to use for evaluating moves
   * @param type The type of heuristic to use
   */
  setHeuristic(type: HeuristicType): void {
    this.heuristic = getHeuristic(type);
    // Also update the MCTS heuristic
    this.mcts.setHeuristic(this.heuristic);
    if (this.verboseLogging) {
    }
  }

  /**
   * Get the current heuristic
   * @returns The current heuristic
   */
  getHeuristic(): HeuristicInterface {
    return this.heuristic;
  }

  /**
   * Get the current heuristic type
   * @returns The current heuristic type
   */
  getHeuristicType(): HeuristicType {
    return this.heuristic.name as HeuristicType;
  }

  /**
   * Enable or disable verbose logging
   * @param isVerbose Whether to enable verbose logging
   */
  setVerboseLogging(isVerbose: boolean): void {
    this.verboseLogging = isVerbose;
    this.mcts.setVerboseLogging(isVerbose);
  }

  /**
   * Get the last AI move statistics
   * @returns Statistics about the last AI move
   */
  getLastMoveStats(): AIStats | null {
    return this.lastMoveStats;
  }

  /**
   * Set the number of search iterations for the MCTS algorithm
   * @param iterations Number of iterations to perform
   */
  setSearchIterations(iterations: number): void {
    this.searchIterations = iterations;
    if (this.verboseLogging) {
    }
  }

  /**
   * Get the number of search iterations
   * @returns Current iteration count
   */
  getSearchIterations(): number {
    return this.searchIterations;
  }

  /**
   * Set the exploration parameter for the MCTS algorithm
   * Higher values encourage exploration of new moves
   * Lower values encourage exploitation of known good moves
   * @param parameter The exploration parameter value (default is Math.sqrt(2))
   */
  setExplorationParameter(parameter: number): void {
    this.mcts.setExplorationParameter(parameter);
    if (this.verboseLogging) {
    }
  }

  /**
   * Enable or disable debug mode for specific heuristics
   * @param enable Whether to enable debug mode
   */
  setHeuristicDebug(enable: boolean): void {
    if (
      this.heuristic.name === "Plain" &&
      typeof (this.heuristic as any).setDebugging === "function"
    ) {
      (this.heuristic as any).setDebugging(enable);
      if (this.verboseLogging) {
      }
    }
  }

  /**
   * Make a move for the AI player
   * @returns True if a move was made, false otherwise
   */
  async makeMove(): Promise<boolean> {
    // Reset the stats before starting a new move
    this.lastMoveStats = {
      nodesByDepth: new Map<number, number>(),
      totalNodesVisited: 0,
      executionTimeMs: 0,
      heuristicUsed: this.heuristic.name,
      maxDepth: this.maxDepth,
    };

    // Save snake's hasShed state before MCTS search
    const snakeState = this.saveSnakeState();

    const startTime = performance.now();

    // Get the best move using MCTS
    const bestMove = this.getBestMove();

    // Track execution time
    const endTime = performance.now();
    const thinkingTimeMs = Math.round(endTime - startTime);
    if (this.lastMoveStats) {
      this.lastMoveStats.executionTimeMs = thinkingTimeMs;
    }

    if (!bestMove) {
      if (this.verboseLogging) {
      }

      return false;
    }

    // Restore snake's hasShed state before actual move
    this.restoreSnakeState(snakeState);

    // Get the pieces at the source position
    const pieces = this.zodiacEngine.getPiecesAt(bestMove.fromPosition);

    if (pieces.length === 0) {
      if (this.verboseLogging) {
      }

      return false;
    }

    // --- Capture FEN before move ---
    const prevFEN = ZodiacFEN.encode(this.zodiacEngine.getGameState());

    // Execute the move in the game engine with all required parameters
    const isMoveMade = await this.zodiacEngine.makeMove(
      bestMove.fromPosition,
      bestMove.move.position,
      pieces[0],
      bestMove.move.toPiece,
      bestMove.move.moveType,
      bestMove.move.sideEffects
    );

    // --- Capture FEN after move ---
    const postFEN = ZodiacFEN.encode(this.zodiacEngine.getGameState());

    // --- Log or export the FENs and move ---
    if (isMoveMade && this.zodiacEngine.getGameState().mode === "TestAI") {
      // Call exportTestAIMoveAnalysis with correct FENs
      if (typeof this.mcts.exportTestAIMoveAnalysis === "function") {
        this.mcts.exportTestAIMoveAnalysis(prevFEN, postFEN, bestMove, 5);
      }
    }

    return isMoveMade;
  }

  /**
   * Save the hasShed state of all snake pieces on the board
   * @returns A map of positions to hasShed values
   */
  private saveSnakeState(): Map<string, boolean> {
    const snakeState = new Map<string, boolean>();
    const gameState = this.zodiacEngine.getGameState();

    // Iterate through the board to find all snake pieces
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const pieces = gameState.board[row][col].Pieces;

        for (const piece of pieces) {
          if (piece.type === "Snake") {
            // Create a key using the position and piece's light/dark status
            const key = `${row}-${col}-${piece.isLight ? "light" : "dark"}`;
            snakeState.set(key, piece.hasShed);
          }
        }
      }
    }

    return snakeState;
  }

  /**
   * Restore the hasShed state of all snake pieces on the board
   * @param snakeState The saved snake state map
   */
  private restoreSnakeState(snakeState: Map<string, boolean>): void {
    const gameState = this.zodiacEngine.getGameState();

    // Iterate through the board to find all snake pieces
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const pieces = gameState.board[row][col].Pieces;

        for (const piece of pieces) {
          if (piece.type === "Snake") {
            // Create the same key used in saveSnakeState
            const key = `${row}-${col}-${piece.isLight ? "light" : "dark"}`;

            if (snakeState.has(key)) {
              const originalHasShed = snakeState.get(key)!;
              if (piece.hasShed !== originalHasShed) {
                piece.hasShed = originalHasShed;
              }
            }
          }
        }
      }
    }
  }

  /**
   * Get the best move for the AI using MCTS
   * @returns Object containing the best move or null if no valid move found
   */
  private getBestMove(): { fromPosition: Position; move: IMove } | null {
    try {
      // Make sure the MCTS is using the current max depth
      this.mcts.setMaxDepth(this.maxDepth);

      // Run MCTS to find the best move
      const bestMove = this.mcts.findBestMove(this.searchIterations);

      // Collect statistics from the MCTS search
      const stats = this.mcts.getNodeStatistics();
      if (this.lastMoveStats) {
        this.lastMoveStats.nodesByDepth = stats.nodesByDepth;
        this.lastMoveStats.totalNodesVisited = stats.totalNodesVisited;
      }

      return bestMove;
    } catch (error) {
      return null;
    }
  }

  /**
   * Provides detailed analysis of the last AI decision process
   * This will show a breakdown of the top moves and why they were chosen
   * @param topMoveCount Number of top moves to analyze (default 3)
   */
  analyzeLastMove(topMoveCount: number = 3): void {
    // Analyze top moves from the previous search
    this.mcts.analyzeTopMoves(topMoveCount);
  }
}
